(() => {
  const configuration = document.currentScript?.dataset || {};
  const mode = configuration.catalogSearchMode;
  const searchPath = configuration.searchUrl;
  if (!mode || !searchPath) return;
  let sequence = 0;
  const enhanced = new WeakSet();
  function enhance(input) {
    if (enhanced.has(input) || input.closest('predictive-search-component')) return;
    enhanced.add(input);
    const list = document.createElement('div');
    list.id = 'catalog-search-options-' + (++sequence);
    list.className = 'catalog-search-suggestions';
    list.setAttribute('role', 'listbox');
    list.hidden = true;
    document.body.append(list);
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', list.id);
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('autocomplete', 'off');
    let timer, controller, version = 0, active = -1, displayedQuery = '';
    function hide() {
      list.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      active = -1;
    }
    function position() {
      const box = input.getBoundingClientRect();
      const width = Math.min(Math.max(box.width, 250), innerWidth - 24);
      list.style.width = width + 'px';
      list.style.left = Math.max(12, Math.min(box.left, innerWidth - width - 12)) + scrollX + 'px';
      list.style.top = box.bottom + scrollY + 4 + 'px';
    }
    function render(items, emptyText, query) {
      list.replaceChildren();
      active = -1;
      displayedQuery = query;
      for (const [index, item] of items.slice(0, 8).entries()) {
        const link = document.createElement('a');
        link.id = list.id + '-' + index;
        link.href = item.url;
        link.className = 'catalog-search-suggestion';
        link.setAttribute('role', 'option');
        link.setAttribute('aria-selected', 'false');
        if (item.image) {
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = '';
          image.width = 48;
          image.height = 48;
          link.classList.add('has-image');
          if (item.image_brand) {
            const frame = document.createElement('span');
            Object.assign(frame.style, {position: 'relative', display: 'block', width: '48px', height: '60px', background: '#fff'});
            Object.assign(image.style, {position: 'absolute', top: '0', left: '0', width: '48px', height: '48px', objectFit: 'contain'});
            const brand = document.createElement('span');
            brand.textContent = item.image_brand;
            brand.setAttribute('aria-hidden', 'true');
            Object.assign(brand.style, {position: 'absolute', bottom: '1px', left: '0', right: '0', textAlign: 'center', font: '700 5px/7px Arial,sans-serif', letterSpacing: '.1em', color: '#78807c'});
            frame.append(image, brand);
            link.append(frame);
          } else link.append(image);
        }
        const label = document.createElement('span');
        if (item.model) {
          const model = document.createElement('strong');
          model.textContent = item.model;
          label.append(model);
        }
        const title = document.createElement('span');
        title.textContent = item.title;
        label.append(title);
        if (item.information_page) {
          const kind = document.createElement('small');
          kind.textContent = 'Product information page';
          label.append(document.createElement("br"), kind);
        }
        link.append(label);
        list.append(link);
      }
      if (!items.length) {
        const message = document.createElement('p');
        message.setAttribute('role', 'status');
        message.textContent = emptyText;
        list.append(message);
      }
      position();
      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }
    async function search(query, requestVersion) {
      controller = new AbortController();
      const requestController = controller;
      const url = new URL(searchPath, location.origin);
      url.searchParams.set('q', query);
      if (mode === 'factory') url.searchParams.set('view', 'json');
      if (window.Shopify?.theme?.role === 'unpublished') url.searchParams.set('preview_theme_id', String(window.Shopify.theme.id));
      try {
        const response = await fetch(url, {signal: requestController.signal, credentials: 'same-origin', cache: 'no-store'});
        if (!response.ok) throw new Error('Search response ' + response.status);
        let items, emptyText;
        if (mode === 'factory') {
          const data = await response.json();
          items = data.results;
          emptyText = data.results_label;
          if (!items.length) {
            const noticeUrl = new URL(url);
            noticeUrl.searchParams.delete('view');
            const noticeResponse = await fetch(noticeUrl, {signal: requestController.signal, credentials: 'same-origin', cache: 'no-store'});
            if (noticeResponse.ok) {
              const noticeDocument = new DOMParser().parseFromString(await noticeResponse.text(), 'text/html');
              emptyText = noticeDocument.querySelector('[data-search-notice]')?.textContent.trim() || emptyText;
            }
          }
        } else {
          const documentResult = new DOMParser().parseFromString(await response.text(), 'text/html');
          const catalogue = documentResult.querySelector('.cer-search');
          if (!catalogue) throw new Error('Catalogue search unavailable');
          items = Array.from(catalogue.querySelectorAll('.cer-search-card')).map(card => ({
            url: card.getAttribute('href'),
            title: card.querySelector('h2')?.textContent.trim() || '',
            model: card.querySelector('strong')?.textContent.trim() || '',
            image: card.querySelector('img')?.getAttribute('src') || null,
            image_brand: card.dataset.imageBrand || '',
            information_page: card.hasAttribute('data-information-page'),
          }));
          emptyText = catalogue.querySelector('[data-search-notice]')?.textContent.trim() || catalogue.querySelector('.cer-search-grid + p')?.textContent || catalogue.querySelector('.cer-search-count')?.textContent || '';
        }
        if (requestController.signal.aborted || requestVersion !== version || input.value.trim() !== query || document.activeElement !== input) return;
        render(items, emptyText, query);
      } catch (error) {
        if (!requestController.signal.aborted && requestVersion === version) hide();
      }
    }
    function schedule(event) {
      if (event?.isComposing) return;
      clearTimeout(timer);
      controller?.abort();
      const requestVersion = ++version;
      const query = input.value.trim();
      hide();
      if (query.length < 2) return;
      timer = setTimeout(() => search(query, requestVersion), 200);
    }
    input.addEventListener('input', schedule);
    input.addEventListener('search', schedule);
    input.addEventListener('focus', () => {if (input.value.trim().length >= 2) schedule();});
    input.addEventListener('blur', () => setTimeout(hide, 150));
    input.form?.addEventListener('submit', () => {clearTimeout(timer); controller?.abort(); hide();});
    input.addEventListener('keydown', event => {
      if (event.isComposing) return;
      const options = Array.from(list.querySelectorAll('[role="option"]'));
      if (event.key === 'Escape') {clearTimeout(timer); controller?.abort(); hide(); return;}
      if (event.key === 'Tab') {hide(); return;}
      if (list.hidden || displayedQuery !== input.value.trim() || !options.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        active = active < 0 ? (event.key === 'ArrowDown' ? 0 : options.length - 1) : (active + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
        options.forEach((option, index) => option.setAttribute('aria-selected', String(index === active)));
        input.setAttribute('aria-activedescendant', options[active].id);
        options[active].scrollIntoView({block: 'nearest'});
      } else if (event.key === 'Enter' && active >= 0) {
        event.preventDefault();
        location.href = options[active].href;
      }
    });
    list.addEventListener('pointerdown', event => {if (event.target.closest('a')) event.preventDefault();});
    addEventListener('resize', () => {if (!list.hidden) position();});
    addEventListener('scroll', () => {if (!list.hidden) position();}, {passive: true});
  }
  function initialize() {
    document.querySelectorAll('form[role="search"] input[name="q"], form[action*="/search"] input[name="q"]').forEach(enhance);
  }
  initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
